<template>
    <div class="carousel">
        <agile class="carousel__main mb-3"
               ref="carousel__main"
               :options="options1"
               :as-nav-for="asNavFor1">
            <div class="slide"
                 v-for="(slide, index) in slides"
                 :key="index"
                 :class="`slide--${index}`">
                <!-- Sketchfab -->
                <div v-if="isSketchfab(slide)" class="embed-wrap">
                    <iframe
                        style="width: 100%; aspect-ratio: 1 / 1;"
                        :title="`Sketchfab model ${index + 1}`"
                        frameborder="0"
                        allowfullscreen
                        mozallowfullscreen="true"
                        webkitallowfullscreen="true"
                        allow="autoplay; fullscreen; xr-spatial-tracking"
                        xr-spatial-tracking
                        execution-while-out-of-viewport
                        execution-while-not-rendered
                        web-share

                        :src="sketchfabEmbedUrl(slide)">
                    </iframe>
                </div>

                <!-- Image -->
                <a v-else :href="slide">
                    <img :src="slide" />
                </a>
            </div>

            <template slot="prevButton"><img src="/img/icons/chevron-left.svg" alt=""/></template>
            <template slot="nextButton"><img src="/img/icons/chevron-right.svg" alt=""/></template>
        </agile>

        <agile class="agile__thumbnails"
               ref="agile__thumbnails"
               :options="options2"
               :as-nav-for="asNavFor2">
            <div class="slide slide--thumbnail"
                 v-for="(slide, index) in slides"
                 :key="index"
                 :class="`slide--${index}`"
                 @click="$refs.agile__thumbnails.goTo(index)">

                <!-- Sketchfab thumbnail -->
                <template v-if="isSketchfab(slide)">
                    <img v-if="sketchfabThumbs[index] && false" :src="sketchfabThumbs[index]" alt="3D model thumbnail" />
                    <div v-else class="thumb-3d" aria-label="3D model">3D</div>
                </template>

                <!-- Image thumbnail -->
                <img v-else :src="slide" />
            </div>
        </agile>
    </div>
</template>

<script>
import { VueAgile } from 'vue-agile'
import baguetteBox from 'baguettebox.js'

export default {
    name: 'CatUxoCarousel',
    components: { agile: VueAgile },
    props: {
        slides: {
            type: Array,
            default: () => [],
            required: true
        }
    },
    data () {
        return {
            asNavFor1: [],
            asNavFor2: [],
            sketchfabThumbs: {},

            options1: {
                dots: false,
                fade: true,
                navButtons: true
            },
            options2: {
                autoplay: false,
                centerMode: false,
                dots: false,
                navButtons: true,
                slidesToShow: 4,
                infinite: false,
                responsive: [
                    { breakpoint: 1000, settings: { navButtons: false } }
                ]
            }
        }
    },
    methods: {
        isSketchfab (url) {
            return typeof url === 'string' && url.includes('sketchfab.com')
        },

        // Works with:
        // - https://sketchfab.com/models/<UID>
        // - https://sketchfab.com/3d-models/<slug>-<UID>
        // - already-embed urls containing /models/<UID>/embed
        sketchfabModelId (url) {
            if (!url) return null

            const embedMatch = url.match(/sketchfab\.com\/models\/([a-f0-9]{32})\/embed/i)
            if (embedMatch) return embedMatch[1]

            const modelsMatch = url.match(/sketchfab\.com\/models\/([a-f0-9]{32})/i)
            if (modelsMatch) return modelsMatch[1]

            const slugMatch = url.match(/sketchfab\.com\/3d-models\/.*-([a-f0-9]{32})/i)
            if (slugMatch) return slugMatch[1]

            return null
        },

        sketchfabEmbedUrl (url) {
            const id = this.sketchfabModelId(url)
            if (!id) return url // fallback: if user passed an embed url already
            return `https://sketchfab.com/models/${id}/embed?autostart=1&ui_inspector=0&ui_controls=0`
        },

        async loadSketchfabThumbs () {
            // Get nice thumbnails via Sketchfab oEmbed (no auth needed)
            // https://sketchfab.com/oembed?url=<modelUrl>
            const requests = this.slides.map(async (slide, index) => {
                if (!this.isSketchfab(slide)) return
                try {
                    const endpoint = `https://sketchfab.com/oembed?url=${encodeURIComponent(slide)}`
                    const res = await fetch(endpoint)
                    if (!res.ok) return
                    const json = await res.json()
                    // oEmbed typically includes thumbnail_url
                    if (json && json.thumbnail_url) {
                        this.$set(this.sketchfabThumbs, index, json.thumbnail_url)
                    }
                } catch (e) {
                    // keep fallback "3D" tile
                }
            })

            await Promise.all(requests)
        }
    },
    async mounted () {
        setTimeout(() => {
            this.$refs.carousel__main.reload()
        }, 50)

        this.asNavFor1.push(this.$refs.agile__thumbnails)
        this.asNavFor2.push(this.$refs.carousel__main)

        // Only run lightbox on images (optional). If you keep it on .carousel__main,
        // it may try to hook the iframe slide too, depending on markup.
        baguetteBox.run('.carousel__main', {
            overlayBackgroundColor: 'rgba(0,0,0,0.2)'
        })

        await this.loadSketchfabThumbs()
    }
}
</script>

<style lang="scss" scoped>
.carousel {
    user-select: none;
    position: relative;
}

.agile__thumbnails {
    margin: rem(0 -4px);
    width: calc(100%);
}

.slide {
    align-items: center;
    box-sizing: border-box;
    display: flex;
    justify-content: center;

    &--thumbnail {
        cursor: pointer;
        height: auto;
        padding: rem(0 4px);
        transition: opacity 0.3s;
        max-width: rem(144px);

        &:hover { opacity: 0.75; }
    }

    img {
        height: 100%;
        object-fit: contain;
        object-position: center;
        width: 100%;
    }
}

.embed-wrap {
    width: 100%;
}

.thumb-3d {
    width: 100%;
    aspect-ratio: 1 / 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    color: black;
    border-radius: rem(6px);
    background-color: white;
}
</style>



// WEBPACK FOOTER //
// resources/js/components/shared/Carousel.vue